// DLNAME: the ONE place the browser turns a fetched blob into a preview or a named download.
//
// THE DEFECT this removes: a site that fetches bytes as a blob and builds an anchor with no
// `download` attribute lets the browser name the saved file after the last segment of the `blob:`
// URL, which is a generated UUID. It reads as a random id. The server's Content-Disposition is
// never consulted on that path, because a `blob:` URL has no response attached to it.
//
// THE NAME COMES FROM THE SERVER, through X-Download-Filename, rather than being rebuilt here.
// One implementation means the browser and the server cannot disagree, and the sanitising rules
// (forbidden characters, trailing dots, length cap, accent handling) live in app/services/
// download_names.py alone instead of being duplicated in JavaScript.

// The fallback used when the server sends no name header. Never an id.
const NEUTRAL_NAME = 'Document';

/**
 * The file name the server chose for this response, decoded.
 * Falls back rather than throwing: a malformed header must not break the download.
 */
export function downloadNameFrom(res, fallback = NEUTRAL_NAME) {
  const raw = res?.headers?.['x-download-filename'];
  if (!raw) return fallback;
  try {
    return decodeURIComponent(raw) || fallback;
  } catch {
    return fallback;
  }
}

/**
 * Save the blob under the server's name.
 *
 * `download` is what makes the browser use the name instead of the blob URL's last segment.
 * `target="_blank"` is deliberately NOT set: it has no effect once `download` is present and the
 * two conflict.
 */
export function saveBlob(res, fallback = NEUTRAL_NAME) {
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = downloadNameFrom(res, fallback);
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

/**
 * Open the blob in a new tab for preview.
 *
 * Unchanged behaviour, kept as a named helper so a preview and a download sit side by side in the
 * calling code and cannot be confused for each other. A preview carries NO name: a blob opened in
 * a tab has no filename mechanism, which is why a separate download control exists at all.
 */
export function previewBlob(res) {
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

/**
 * PADPREVIEW: the server's own explanation for a FAILED blob request.
 *
 * THE DEFECT THIS REMOVES. With `responseType: 'blob'`, axios parses the ERROR body as a blob too,
 * so `err.response.data` is a Blob rather than the parsed JSON. Reading `.detail` off it always
 * yields undefined, and every one of these call sites silently fell back to its generic sentence.
 * The server's readable reason ("This attachment could not be retrieved from document storage...")
 * was present on the wire and thrown away before anyone could read it.
 *
 * Returns the server's `detail` when there is one, otherwise the caller's fallback. Never throws
 * and never returns an empty string: a failure message is not worth a second failure.
 */
export async function blobErrorDetail(err, fallback = 'Could not open that document.') {
  const data = err?.response?.data;
  if (!data) return fallback;
  // Not a blob response (some callers retry without responseType): already parsed.
  if (typeof data === 'object' && typeof data.detail === 'string' && data.detail) {
    return data.detail;
  }
  if (typeof data.text !== 'function') return fallback;
  try {
    const text = await data.text();
    const parsed = JSON.parse(text);
    return (typeof parsed?.detail === 'string' && parsed.detail) ? parsed.detail : fallback;
  } catch {
    // Not JSON, unreadable, or an empty body. The fallback is the honest answer.
    return fallback;
  }
}
